<template>
  <div class="horizon-grid" data-testid="horizon-grid">
    <div class="horizon-grid__scroll" data-testid="horizon-grid__scroll">
      <table class="horizon-grid__table">
        <thead class="tableHeaderBg">
          <tr>
            <th class="horizon-grid__row-label-header sticky-col">
              <v-menu
                v-model="groupByMenuOpen"
                offset-y
                :close-on-content-click="false"
                transition="slide-y-transition"
                content-class="horizon-grid__group-by-menu"
                data-testid="horizon-grid__group-by-menu"
              >
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="horizon-grid__group-by-trigger"
                    v-bind="attrs"
                    v-on="on"
                    data-testid="horizon-grid__group-by"
                  >
                    <div class="horizon-grid__group-by-caption">Group By</div>
                    <span class="horizon-grid__group-by-value">
                      {{ groupByColumnLabel }}
                      <img
                        src="/img/icons/downArrow.svg"
                        alt="Down Arrow"
                      />
                    </span>
                  </button>
                </template>

                <v-card class="horizon-grid__group-by-card" data-testid="horizon-grid__group-by-card">
                  <v-radio-group
                    v-model="pendingGroupBy"
                    hide-details
                    class="horizon-grid__group-by-radio-group ma-0 pa-0"
                  >
                    <v-list dense class="horizon-grid__group-by-list pa-0">
                      <v-list-item
                        v-for="option in groupByOptions"
                        :key="option.value"
                        class="horizon-grid__group-by-item"
                        @click="pendingGroupBy = option.value"
                        data-testid="horizon-grid__group-by-option"
                      >
                        <v-list-item-action class="horizon-grid__group-by-item-action ma-0">
                          <v-radio
                            :value="option.value"
                            :ripple="false"
                            off-icon="mdi-radiobox-blank"
                            on-icon="mdi-radiobox-marked"
                          />
                        </v-list-item-action>
                        <v-list-item-content class="py-0">
                          <v-list-item-title class="horizon-grid__group-by-item-label">
                            {{ option.label }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-radio-group>

                  <v-divider />

                  <div class="horizon-grid__group-by-actions">
                    <v-btn
                      class="horizon-grid__group-by-apply text-none"
                      depressed
                      @click="applyGroupBy"
                      data-testid="horizon-grid__group-by-apply"
                    >
                      Apply
                    </v-btn>
                  </div>
                </v-card>
              </v-menu>
            </th>
            <th
              v-for="col in displayColumns"
              :key="col.key"
              :class="columnHeaderClass(col)"
              :data-testid="columnHeaderTestId(col)"
            >
              <template v-if="col.type === 'weekend-stub'">
                <span class="horizon-grid__weekend-stub-label">Sat. Sun</span>
              </template>
              <template v-else>
                <div class="horizon-grid__date-header">
                  <span
                    class="horizon-grid__date-header-top"
                    :class="{ 'horizon-grid__date-header-top--weekend': isWeekend(col.dateKey) }"
                  >
                    {{ formatHeaderMonthDay(col.dateKey) }}
                  </span>
                  <div
                    class="horizon-grid__date-header-day"
                    :class="{ 'horizon-grid__date-header-day--today': isToday(col.dateKey) }"
                  >
                    {{ formatHeaderDay(col.dateKey) }}
                  </div>
                </div>
              </template>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="isGridEmpty">
            <tr
              class="horizon-grid__empty-row"
              data-testid="horizon-grid__empty"
            >
              <td
                :colspan="emptyColspan"
                class="horizon-grid__empty-cell"
                data-testid="horizon-grid__empty-cell"
              >
                <div class="horizon-grid__empty-content">
                  <img src="/img/icons/fbc-icons/NoTaskScheduled.svg" alt="No Data" class="horizon-grid__empty-icon mb-2">
                  <p
                    class="horizon-grid__empty-title mb-2"
                    data-testid="horizon-grid__empty-title"
                  >
                    {{ emptyStateTitle }}
                  </p>
                  <p
                    class="horizon-grid__empty-subtitle"
                    data-testid="horizon-grid__empty-subtitle"
                  >
                    {{ emptyStateSubtitle }}
                  </p>
                </div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="row in rows" :key="row.groupKey" data-testid="horizon-grid__row">
            <td class="horizon-grid__row-label sticky-col" data-testid="horizon-grid__row-label">
              <div class="horizon-grid__row-label-main">
                <span class="horizon-grid__row-label-text">{{ row.groupLabel }}</span>
                <span class="horizon-grid__row-count-badge">{{ row.rowTotalCount }}</span>
              </div>
            </td>
            <td
              v-for="col in displayColumns"
              :key="`${row.groupKey}-${col.key}`"
              :class="cellClass(row, col)"
              data-testid="horizon-grid__cell"
              @click="onCellClick(row, col)"
            >
              <div
                v-if="col.type !== 'weekend-stub' && cellHasTasks(row, col.dateKey)"
                class="horizon-grid__cell-content"
              >
                <div class="horizon-grid__cell-count">
                  {{ cellData(row, col.dateKey).taskCount }}
                </div>
                <lb-progress-circle
                  :value="cellCompletion(row, col.dateKey)"
                  :size="28"
                  :width="3"
                />
              </div>
              <div v-else class="horizon-grid__cell-empty" aria-hidden="true">
                <span class="horizon-empty-dash" />
              </div>
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import './HorizonGrid.scss';
import dayjs from 'dayjs';
import { GROUP_BY_OPTIONS } from '../../../utils/horizonConstants';
import { isWeekend, buildHorizonDisplayColumns } from '../../../utils/horizonHelpers';

/** Presentational time-axis grid — rows × date columns; emits cell-click and group-by (see horizon-plan). */
export default {
  name: 'HorizonGrid',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    dateColumns: {
      type: Array,
      default: () => [],
    },
    groupBy: {
      type: String,
      default: 'location',
    },
    cycleStart: {
      type: String,
      default: '',
    },
    cycleEnd: {
      type: String,
      default: '',
    },
    selectedCell: {
      type: Object,
      default: null,
    },
    todayKey: {
      type: String,
      default: '',
    },
    /** True when status tab or chip filters are applied (empty grid due to filtering). */
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
    /** True when the checklist still has tasks before Horizon scoping. */
    hasSourceData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      groupByOptions: GROUP_BY_OPTIONS,
      groupByMenuOpen: false,
      pendingGroupBy: this.groupBy,
    };
  },
  computed: {
    /** Human-readable label for the active group-by column header. */
    groupByColumnLabel() {
      const match = GROUP_BY_OPTIONS.find((opt) => opt.value === this.groupBy);
      return match?.label || 'Group By';
    },
    /** Collapsed weekend columns + day columns for header/body alignment. */
    displayColumns() {
      return buildHorizonDisplayColumns(this.dateColumns, this.rows);
    },
    /** True when the grid has no grouped rows to render. */
    isGridEmpty() {
      return !Array.isArray(this.rows) || this.rows.length === 0;
    },
    /** Column span for the empty-state row (group-by + date columns). */
    emptyColspan() {
      return 1 + this.displayColumns.length;
    },
    /** True when filters removed all rows but source checklist tasks still exist. */
    isFilteredEmpty() {
      return this.isGridEmpty && this.hasActiveFilters && this.hasSourceData;
    },
    /** True when the selected date range has no tasks but the cycle still has tasks. */
    isDateRangeEmpty() {
      return this.isGridEmpty && !this.hasActiveFilters && this.hasSourceData;
    },
    /** Icon for the grid empty state. */
    emptyStateIcon() {
      return this.isFilteredEmpty ? 'mdi-filter-off-outline' : 'mdi-table-off';
    },
    /** Primary empty-state message. */
    emptyStateTitle() {
      return this.isFilteredEmpty ? 'No data for applied filters' : 'No data to show';
    },
    /** Secondary empty-state helper text. */
    emptyStateSubtitle() {
      if (this.isFilteredEmpty) {
        return 'Try adjusting or clearing your filters to see more results.';
      }
      if (this.isDateRangeEmpty) {
        return 'There are no tasks in the selected date range.';
      }
      return 'There is no data available at this time.';
    },
  },
  watch: {
    groupByMenuOpen(isOpen) {
      if (isOpen) {
        this.pendingGroupBy = this.groupBy;
      }
    },
    groupBy(value) {
      if (!this.groupByMenuOpen) {
        this.pendingGroupBy = value;
      }
    },
  },
  methods: {
    /** Emits group-by-change with the pending selection and closes the menu. */
    applyGroupBy() {
      this.$emit('group-by-change', this.pendingGroupBy);
      this.groupByMenuOpen = false;
    },
    isWeekend,
    /** True if dateKey matches the todayKey prop (today column highlight). */
    isToday(dateKey) {
      return dateKey === this.todayKey;
    },
    /** True if row is the first row in the grid (used for today empty-cell click). */
    isFirstRow(row) {
      return this.rows.length > 0 && this.rows[0].groupKey === row.groupKey;
    },
    /** True if cell has tasks or is today's cell on the first row (selectable empty state). */
    isCellClickable(row, dateKey) {
      if (this.cellHasTasks(row, dateKey)) return true;
      return this.isToday(dateKey) && this.isFirstRow(row);
    },
    /** Formats column header as "MMM . ddd" (e.g. Jun . Mon). */
    formatHeaderMonthDay(dateKey) {
      const d = dayjs(dateKey);
      return `${d.format('MMM')} . ${d.format('ddd')}`;
    },
    /** Formats column header day-of-month number. */
    formatHeaderDay(dateKey) {
      return dayjs(dateKey).format('D');
    },
    /** CSS classes for a date column header (today, weekend, weekend-stub). */
    columnHeaderClass(col) {
      const classes = ['horizon-grid__column-header'];
      if (col.type === 'weekend-stub') {
        classes.push('horizon-grid__column-header--weekend-stub');
        if (this.weekendStubIncludesToday(col)) {
          classes.push('horizon-grid__column-header--today');
        }
        return classes;
      }
      const dateKey = col.dateKey;
      if (this.isToday(dateKey)) classes.push('horizon-grid__column-header--today');
      if (this.isWeekend(dateKey)) classes.push('horizon-grid__column-header--weekend');
      return classes;
    },
    /** True if a collapsed weekend-stub column includes today's date. */
    weekendStubIncludesToday(col) {
      return (col.dateKeys || []).some((dateKey) => this.isToday(dateKey));
    },
    /** data-testid for column header (today vs default). */
    columnHeaderTestId(col) {
      if (col.type === 'weekend-stub') {
        return this.weekendStubIncludesToday(col)
          ? 'horizon-grid__today-col-header'
          : 'horizon-grid__column-header';
      }
      return this.isToday(col.dateKey)
        ? 'horizon-grid__today-col-header'
        : 'horizon-grid__column-header';
    },
    /** Returns cell payload for a row/dateKey with safe defaults. */
    cellData(row, dateKey) {
      return row.cells?.[dateKey] || { taskCount: 0, tasks: [], completionPercentage: 0 };
    },
    /** True if the cell has one or more tasks. */
    cellHasTasks(row, dateKey) {
      return this.cellData(row, dateKey).taskCount > 0;
    },
    /** Approved-task percentage for a grid cell progress ring. */
    cellCompletion(row, dateKey) {
      return this.cellData(row, dateKey).completionPercentage || 0;
    },
    /** True if weekend column is empty and rendered as a collapsed stub. */
    isCollapsedWeekendCell(row, dateKey) {
      if (!this.isWeekend(dateKey)) return false;
      return !this.cellHasTasks(row, dateKey);
    },
    /** CSS classes for a grid body cell (empty, today, selected, weekend-stub). */
    cellClass(row, col) {
      const classes = ['horizon-grid__cell'];
      if (col.type === 'weekend-stub') {
        classes.push('horizon-grid__cell--weekend-stub');
        return classes;
      }
      const dateKey = col.dateKey;
      if (!this.cellHasTasks(row, dateKey)) classes.push('horizon-grid__cell--empty');
      if (this.isToday(dateKey)) classes.push('horizon-grid__cell--today');
      if (this.isToday(dateKey) && this.isFirstRow(row)) {
        classes.push('horizon-grid__cell--today-first');
      }
      if (this.isCollapsedWeekendCell(row, dateKey)) {
        classes.push('horizon-grid__cell--weekend-stub');
      }
      const selected = this.selectedCell;
      if (selected && selected.dateKey === dateKey) {
        const isColumnSelection = selected.groupKey == null;
        if (isColumnSelection || selected.groupKey === row.groupKey) {
          classes.push('horizon-grid__cell--selected');
        }
      }
      return classes;
    },
    /** Emits cell-click when user selects a clickable grid cell. */
    onCellClick(row, col) {
      if (col.type === 'weekend-stub') return;
      if (this.isCollapsedWeekendCell(row, col.dateKey)) return;
      if (!this.isCellClickable(row, col.dateKey)) return;
      this.$emit('cell-click', {
        groupKey: row.groupKey,
        dateKey: col.dateKey,
        cellData: this.cellData(row, col.dateKey),
      });
    },
  },
};
</script>
